import { appUrls } from "../../../EasyProjectsCommon/AppUrls";
import { XhrDeferredType } from "../../../EasyProjectsCommon/General";
import { reloadAvailableUsersRequest } from "../../../Redux/AvailableUsers/users/actions";
import { uniqueKey } from "../../../utils/uniqueKey";
import { EntityCache } from "./EntityCache";

export type UserInfo = {
    UserId: number;
    Name: string;
    IsDisabledUser: boolean;
    Email: string;
    RoleId: number;
};

type InvalidateCacheOptions = {
    reloadImmediately: boolean;
};

export class UsersCache extends EntityCache<UserInfo> {
    getAllUrl: string;
    keyName: keyof UserInfo = "UserId";

    constructor() {
        super();

        this.getAllUrl = UsersCache.getEntitiesUrl;

        this.ensureLoad();
    }

    static get inst(): UsersCache {
        return UsersCache._instance || (UsersCache._instance = new UsersCache());
    }

    static get getEntitiesUrl(): string {
        return `${appUrls.reference.getVisibleUsers}?version=${ep.settings.cacheVersions.usersVersion}`;
    }

    static invalidate(options: InvalidateCacheOptions = { reloadImmediately: false }) {
        const newVersion = uniqueKey();
        ep.settings.cacheVersions.usersVersion = newVersion;
        if (options.reloadImmediately) {
            const newCacheInstance = new UsersCache();
            newCacheInstance.ensureLoad().then(() => {
                if (newVersion === ep.settings.cacheVersions.usersVersion) {
                    UsersCache._instance = newCacheInstance;
                    // availableUsers module uses UsersCache as data source
                    // so we need to update it too
                    window.store?.dispatch(reloadAvailableUsersRequest());
                }
            });
        } else {
            UsersCache.reset();
        }
    }

    getAll() {
        if (ep.settings.isGuest) {
            return $.Deferred()
                .resolve([
                    {
                        UserId: ep.settings.currentUserId,
                        Name: ep.settings.currentUserName,
                    } as UserInfo,
                ])
                .promise() as XhrDeferredType;
        }

        return super.getAll();
    }

    /**
     * Loads users with specified ids. If there is no such users (or no permissions to view them) then
     * promise won't be failed and return just existing users.
     */
    loadSafely = (ids: number[]) => {
        return this.loadEntities(ids, null, true).done(this._mergeEntities);
    };

    protected loadEntities(ids: number[], taskId?: number, ignoreNotFound?: boolean): JQuery.Promise<UserInfo[]> {
        if (ep.settings.isGuest || !ids.length) {
            return this._getEmptyResult();
        }

        return ep.common.postJson(appUrls.reference.getUsersInfo, {
            ids,
            taskId,
            ignoreNotFound: !!ignoreNotFound,
        });
    }

    protected throwDataNotLoaded(): void {
        throw new Error("Users data for cache are not loaded yet");
    }
}
